// Performance checklists (#2020) — the department's own pass/fail task sheets,
// authored on a lesson plan and assessed per member on a training record that
// uses the plan.
//
// Deliberately the same shape as the VIERA / J&B 1010 skill sheets the app
// already signs off (see embertracking/src/data/jprSteps.js): an ordered list
// of task steps, each marked P or F, where every step has to pass. The one
// addition is a heading row — a paper sheet's setup and "exercise complete"
// lines describe the evolution rather than assess it, and a heading lets the
// author keep them on the sheet without making them something to pass.
//
// Stored on the plan as
//   performanceChecklist: {instructions: string, items: [{id, type, text}]}
// Item ids are generated when a row is added and never reused, so an
// assessment can key its marks by id and survive the steps being reordered.

const STEP = 'step';
const HEADING = 'heading';
const ITEM_TYPES = [STEP, HEADING];

// Generous for any real sheet (the SCBA grinder sheet that prompted this has
// 20 rows), small enough to keep a plan doc and its PDF bounded.
const MAX_ITEMS = 200;
const MAX_ITEM_TEXT = 1000;
const MAX_INSTRUCTIONS = 5000;

const cleanText = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');

// The checklist as stored: trimmed text, blank rows dropped, unknown types read
// as steps. Anything that isn't a checklist normalises to an empty one, so
// callers never have to null-check. `makeId` fills in a row that arrives
// without an id (an import draft); saved rows always carry one.
const normalizePerformanceChecklist = (value, {makeId} = {}) => {
    const rawItems = Array.isArray(value?.items) ? value.items : [];
    const items = [];
    for (const item of rawItems) {
        if (items.length >= MAX_ITEMS) break;
        const text = cleanText(item?.text, MAX_ITEM_TEXT);
        if (!text) continue;
        const id = typeof item?.id === 'string' && item.id ? item.id : (makeId ? makeId() : null);
        if (!id) continue;
        items.push({id, type: item?.type === HEADING ? HEADING : STEP, text});
    }
    return {instructions: cleanText(value?.instructions, MAX_INSTRUCTIONS), items};
};

// The rows that get marked, in sheet order.
const checklistSteps = checklist => (Array.isArray(checklist?.items) ? checklist.items : [])
    .filter(item => item && item.type !== HEADING);

// A plan has a checklist only once it has something to assess. Headings or
// instructions on their own aren't a sheet anyone can pass.
const hasPerformanceChecklist = plan => checklistSteps(plan?.performanceChecklist).length > 0;

// --- assessment ------------------------------------------------------------
//
// One member's attempt at the sheet is a map of marks keyed by item id
// ({[itemId]: 'P'|'F'}) — keyed by id rather than position so a sheet edited
// between attempts can't shift a mark onto the wrong step.
//
// The result follows the VIERA / 1010 rules the app already applies to skill
// sheets: nothing to report until every step is marked; then one F fails it.

const PASS = 'P';
const FAIL = 'F';

const ASSESSMENT_NOT_STARTED = 'NOT_STARTED';
const ASSESSMENT_IN_PROGRESS = 'IN_PROGRESS';
const ASSESSMENT_PASS = 'PASS';
const ASSESSMENT_FAIL = 'FAIL';

const assessmentProgress = (marks, steps) => {
    let marked = 0;
    let failed = 0;
    for (const step of steps || []) {
        const mark = marks?.[step.id];
        if (mark === PASS || mark === FAIL) marked++;
        if (mark === FAIL) failed++;
    }
    return {marked, failed, total: (steps || []).length};
};

const deriveAssessmentStatus = (attempt, steps) => {
    if (!attempt) return ASSESSMENT_NOT_STARTED;
    const {marked, failed, total} = assessmentProgress(attempt.marks, steps);
    if (!total || !marked) return ASSESSMENT_NOT_STARTED;
    if (marked < total) return ASSESSMENT_IN_PROGRESS;
    return failed ? ASSESSMENT_FAIL : ASSESSMENT_PASS;
};

module.exports = {
    PERFORMANCE_CHECKLIST_PASS: PASS,
    PERFORMANCE_CHECKLIST_FAIL: FAIL,
    ASSESSMENT_NOT_STARTED,
    ASSESSMENT_IN_PROGRESS,
    ASSESSMENT_PASS,
    ASSESSMENT_FAIL,
    assessmentProgress,
    deriveAssessmentStatus,
    PERFORMANCE_CHECKLIST_STEP: STEP,
    PERFORMANCE_CHECKLIST_HEADING: HEADING,
    PERFORMANCE_CHECKLIST_ITEM_TYPES: ITEM_TYPES,
    PERFORMANCE_CHECKLIST_MAX_ITEMS: MAX_ITEMS,
    normalizePerformanceChecklist,
    checklistSteps,
    hasPerformanceChecklist
};
